import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Printer, FileText, LoaderCircle } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { printCompositionPref } from "@/lib/rxPrint";
import { toast } from "sonner";
import { fmtDateTime, fmtDate } from "@emr/shared/india";
import { createReferralPdf, type ReferralPdfData } from "@/lib/pdf/referralPdf";
import { createCertificatePdf, type CertificatePdfData } from "@/lib/pdf/certificatePdf";
import { createVisitSummaryPdf, type VisitSummaryData } from "@/lib/pdf/visitSummaryPdf";
import {
  createInvestigationSheetPdf,
  classifyInvestigation,
  type InvestigationPdfData,
  type InvestigationCategory,
} from "@/lib/pdf/investigationPdf";
import { createPrescriptionPdf, type PrescriptionPdfData } from "@/lib/pdf/prescriptionPdf";
import { createInvoicePdf, type InvoicePdfData } from "@/lib/pdf/invoicePdf";

export type ArchiveDocType = "referralLetters" | "medicalCertificates" | "visits" | "investigationRequests" | "prescriptions" | "invoices";

export interface ArchiveItem {
  id: string;
  patientId?: string | null;
  visitId?: string | null;
  patientName: string | null;
  clinicUhid: string | null;
  preview: string;
  savedToR2?: boolean;
  r2Key?: string | null;
  createdAt: string;
  [key: string]: unknown;
}

const TYPE_LABELS: Record<ArchiveDocType, string> = {
  referralLetters: "Referral Letter",
  medicalCertificates: "Medical Certificate",
  visits: "Visit Summary",
  investigationRequests: "Investigation Request",
  prescriptions: "Prescription",
  invoices: "Invoice",
};

export function ReportPreviewDialog({
  open,
  onOpenChange,
  type,
  item,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  type: ArchiveDocType;
  item: ArchiveItem | null;
}) {
  const [printing, setPrinting] = useState(false);

  const reprint = async () => {
    if (!item) return;
    setPrinting(true);
    try {
      const doc = await generatePdf(type, item.id);
      if (doc) {
        const fileName = `${TYPE_LABELS[type].toLowerCase().replace(/\s/g, "-")}-${item.id.slice(0, 8)}.pdf`;
        doc.save(fileName);
        toast.success("PDF generated");
      }
    } catch (e) {
      const err = e as Error & { body?: { error?: string; message?: string } };
      toast.error(err?.body?.error || err?.body?.message || err?.message || "Failed to generate PDF");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-maroon-700 dark:text-amber-500">
            <FileText size={18} />
            {TYPE_LABELS[type]}
          </DialogTitle>
          <DialogDescription>Document preview — review metadata then reprint.</DialogDescription>
        </DialogHeader>
        {!item ? (
          <Skeleton className="h-40" />
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <MetaRow label="Patient" value={item.patientName ?? "—"} />
              <MetaRow label="UHID" value={item.clinicUhid ?? "—"} />
              <MetaRow label="Date" value={fmtDateTime(item.createdAt)} />
              {item.savedToR2 !== undefined && (
                <MetaRow label="R2" value={item.savedToR2 ? "Saved" : "Not saved"} />
              )}
              <MetaRow label="R2 Key" value={item.r2Key ?? "—"} />
            </div>
            <div className="rounded-lg border border-cream-200 bg-cream-50 p-3">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted">Preview</div>
              <p className="text-sm text-ink whitespace-pre-wrap">{item.preview || "—"}</p>
            </div>
            {(item as { number?: string }).number && (
              <div className="text-sm">
                <span className="text-muted">Invoice #: </span>
                <Badge variant="outline" className="font-mono">{(item as unknown as { number: string }).number}</Badge>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={reprint} disabled={printing || !item} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
            {printing ? <LoaderCircle size={16} className="mr-1 animate-spin" /> : <Printer size={16} className="mr-1" />}
            Reprint
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted">{label}</div>
      <div className="text-sm text-ink">{value}</div>
    </div>
  );
}

async function generatePdf(type: ArchiveDocType, id: string): Promise<import("jspdf").jsPDF | null> {
  if (type === "referralLetters") {
    const { letter } = await apiFetch<{ letter: Record<string, unknown> }>(`/referrals/${id}`);
    return createReferralPdf(letter as unknown as ReferralPdfData);
  }
  if (type === "medicalCertificates") {
    const { certificate } = await apiFetch<{ certificate: Record<string, unknown> }>(`/certificates/${id}`);
    return createCertificatePdf(certificate as unknown as CertificatePdfData);
  }
  if (type === "visits") {
    const agg = await apiFetch<VisitSummaryData>(`/visits/${id}`);
    return createVisitSummaryPdf(agg);
  }
  if (type === "investigationRequests") {
    // investigationRequest + catalog list + visit are all keyed by the SAME
    // known id (visit id) — no data dependency between the three legs —
    // parallel per branch (2026-10-31 Batch 4 batching pass).
    const [{ investigationRequest }, invs, visit] = await Promise.all([
      apiFetch<{ investigationRequest: Record<string, unknown> | null }>(`/visits/${id}/investigations`),
      apiFetch<{ rows: { id: string; name: string; category: string | null }[] }>(`/investigations`),
      apiFetch<{ visit: Record<string, unknown> }>(`/visits/${id}`),
    ]);
    if (!investigationRequest) throw new Error("Investigation request not found");
    const selectedIds = (investigationRequest.selectedInvestigationIds as string[]) ?? [];
    // Heading category mirrors the primary print path (emr-visit.tsx):
    // rows classify via classifyInvestigation (null/other → Lab); a uniform
    // selection reprints under its real category, mixed/empty falls back
    // to "Lab" (classifyInvestigation's own fallback).
    const selectedRows = invs.rows.filter((r) => selectedIds.includes(r.id));
    const selectedTests = selectedRows.map((r) => ({ id: r.id, name: r.name }));
    const categories = new Set(selectedRows.map((r) => classifyInvestigation(r.category)));
    const headingCategory: InvestigationCategory =
      categories.size === 1 ? [...categories][0] : "Lab";
    const data: InvestigationPdfData = {
      patientName: (visit.visit.patientName as string) ?? "—",
      clinicUhid: (visit.visit.clinicUhid as string) ?? "—",
      patientDob: (visit.visit.patientDob as string) ?? null,
      patientGender: (visit.visit.patientGender as string) ?? null,
      checkInAt: (visit.visit.checkInAt as string) ?? "",
      diagnosis: (investigationRequest.diagnosis as string) ?? null,
      selectedTests,
      notes: (investigationRequest.notes as string) ?? null,
      doctorName: (visit.visit.doctorName as string) ?? null,
      doctorRegNumber: (visit.visit.doctorRegNumber as string) ?? null,
    };
    return createInvestigationSheetPdf(data, headingCategory);
  }
  if (type === "prescriptions") {
    const visitId = id;
    // prescriptions + visit + notes are all keyed by the SAME known visit id
    // — no data dependency between the three legs — parallel per branch
    // (2026-10-31 Batch 4 batching pass).
    const [{ rows }, visit, { note }] = await Promise.all([
      apiFetch<{ rows: { visitId: string; medicines: { name: string; composition: string | null; strength: string | null; frequency: string | null; taper: { fromDate: string; frequency: string; days: number; instructions: string | null }[] | null; duration: string | null; instructions: string | null; serialNo: number }[]; createdAt: string }[] }>(`/visits/${visitId}/prescriptions`),
      apiFetch<{ visit: Record<string, unknown> }>(`/visits/${visitId}`),
      apiFetch<{ note: { diagnosis: string | null; icd10Codes: { code: string; description: string }[] | null } | null }>(`/visits/${visitId}/notes`),
    ]);
    if (rows.length === 0) throw new Error("Prescription not found");
    const rx = rows[0];
    const data: PrescriptionPdfData = {
      patientName: (visit.visit.patientName as string) ?? "—",
      clinicUhid: (visit.visit.clinicUhid as string) ?? "—",
      patientDob: (visit.visit.patientDob as string) ?? null,
      patientGender: (visit.visit.patientGender as string) ?? null,
      checkInAt: (visit.visit.checkInAt as string) ?? "",
      diagnosis: note?.diagnosis ?? null,
      medicines: rx.medicines.map((m) => ({
        serialNo: m.serialNo,
        name: m.name,
        composition: m.composition,
        strength: m.strength,
        frequency: m.frequency,
        taper: m.taper,
        duration: m.duration,
        instructions: m.instructions,
      })),
      showComposition: printCompositionPref(),
      reviewNeeded: Boolean(visit.visit.reviewNeeded),
      reviewDate: (visit.visit.reviewDate as string) ?? null,
      doctorName: (visit.visit.doctorName as string) ?? null,
      doctorQualifications: (visit.visit.doctorQualifications as string) ?? null,
      doctorRegNumber: (visit.visit.doctorRegNumber as string) ?? null,
    };
    return createPrescriptionPdf(data);
  }
  if (type === "invoices") {
    const { invoice } = await apiFetch<{ invoice: Record<string, unknown> }>(`/invoices/${id}`);
    return createInvoicePdf(invoice as unknown as InvoicePdfData);
  }
  return null;
}

export { fmtDate };
